import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SprintEntity } from '@data';
import { ClearFilterButton, FilterField } from '@app/components/core';
import { filterBarClass } from '@app/constants/styles';
import { formatDateString } from '@app/utils';
import { Language } from '@app/constants/enums';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import {
  INCIDENT_STATUSES,
  IncidentFilters,
  IncidentStatus,
  PRIORITIES,
  Priority,
  hasIncidentFilters,
  noIncidentFilters,
} from './sources/incidents';

interface Props {
  products: { id: string; name: string }[];
  /** Only the sprints something was reported in, newest first: an empty choice is a dead end. */
  sprints: SprintEntity[];
  value: IncidentFilters;
  onChange: (next: IncidentFilters) => void;
}

/** The one filter bar every incident list carries, so they narrow the same way everywhere. */
const IncidentFilterBar = ({ products, sprints, value, onChange }: Props): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const set = (patch: Partial<IncidentFilters>): void => onChange({ ...value, ...patch });

  // Code and meaning apart, so the trigger stays readable as "P1, P2" when two are ticked.
  const priorityOptions = PRIORITIES.map(priority => ({
    value: priority,
    label: priority,
    description: t(`incidents.priorityName.${priority}`),
  }));

  // Two states, because how late a line is against its target is what the row itself already says.
  const statusOptions = INCIDENT_STATUSES.map(status => ({
    value: status,
    label: t(status === 'resolved' ? 'incidents.statusResolved' : 'incidents.statusUnresolved'),
  }));

  const sprintOptions = sprints.map(sprint => ({
    value: sprint.id,
    label: `${t('sprint')} ${sprint.number}`,
    description: `${formatDateString(sprint.startDate, language as Language, 'dd MMM')} - ${formatDateString(
      sprint.endDate,
      language as Language,
      'dd MMM yyyy',
    )}`,
  }));

  return (
    <div className={filterBarClass}>
      <FilterField>
        <MultiSelectField
          label={t('incidents.product')}
          options={products.map(product => ({ value: product.id, label: product.name }))}
          value={value.productIds}
          onChange={productIds => set({ productIds })}
          emptyLabel={t('incidents.allProducts')}
          searchPlaceholder={t('search')}
          emptySearchLabel={t('noMatches')}
          renderCountSummary={count => t('nItemsSelected', { count })}
        />
      </FilterField>

      {/* Wide, because the panel takes the field's width and `Major function b…` picks nothing. */}
      <FilterField wide>
        <MultiSelectField
          label={t('incidents.priority')}
          options={priorityOptions}
          value={value.priorities}
          onChange={next => set({ priorities: next as Priority[] })}
          emptyLabel={t('incidents.allPriorities')}
          searchable={false}
          renderCountSummary={count => t('nItemsSelected', { count })}
        />
      </FilterField>

      <FilterField>
        <MultiSelectField
          label={t('status')}
          options={statusOptions}
          value={value.statuses}
          onChange={next => set({ statuses: next as IncidentStatus[] })}
          emptyLabel={t('allStatuses')}
          searchable={false}
          renderCountSummary={count => t('nItemsSelected', { count })}
        />
      </FilterField>

      <FilterField>
        <MultiSelectField
          label={t('sprint')}
          options={sprintOptions}
          value={value.sprints.map(sprint => sprint.id)}
          onChange={ids => set({ sprints: sprints.filter(sprint => ids.includes(sprint.id)) })}
          emptyLabel={t('incidents.allSprints')}
          searchPlaceholder={t('search')}
          emptySearchLabel={t('noMatches')}
          renderCountSummary={count => t('nItemsSelected', { count })}
        />
      </FilterField>

      {hasIncidentFilters(value) && <ClearFilterButton onClick={() => onChange(noIncidentFilters())} />}
    </div>
  );
};

export default IncidentFilterBar;
